// OCPP 1.6 charging-profile payloads for chargers that do not stack TxDefault
// (Sicon EVDS 1.4.1.5c replaces the previous TxDefault instead of keeping stack 0).

export const FALLBACK_HOLD_SECS = 7 * 24 * 3600; // 7 days
export const MIN_OVERLAY_SECS = 60;
export const PROFILE_STORAGE_PREFIX = 'ocpp.chargingProfile.';

function asInt(value, fallback = 0) {
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : fallback;
}

function asLimit(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : NaN;
}

function requireLimit(value, label) {
  const n = asLimit(value);
  if (!Number.isFinite(n) || n < 0) {
    throw new Error(`${label} must be a non-negative number`);
  }
  return n;
}

function period(startPeriod, limit, numberPhases) {
  return {
    startPeriod: asInt(startPeriod, 0),
    limit,
    numberPhases: asInt(numberPhases, 3),
  };
}

function withScheduleTimes(profile, now, durationSec) {
  const validTo = new Date(now.getTime() + durationSec * 1000);
  profile.chargingSchedule.startSchedule = now.toISOString();
  profile.chargingSchedule.duration = durationSec;
  profile.validTo = validTo.toISOString();
  return profile;
}

export function profileStorageKey(chargerId) {
  return `${PROFILE_STORAGE_PREFIX}${chargerId}`;
}

export function loadSavedProfileLimits(chargerId) {
  if (!chargerId || typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(profileStorageKey(chargerId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const saved = {};
    if (parsed.fallback_limit != null) saved.fallback_limit = parsed.fallback_limit;
    if (parsed.overlay_limit != null) saved.overlay_limit = parsed.overlay_limit;
    return Object.keys(saved).length ? saved : null;
  } catch {
    return null;
  }
}

export function saveProfileLimits(chargerId, form) {
  if (!chargerId || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(profileStorageKey(chargerId), JSON.stringify({
      fallback_limit: form.fallback_limit,
      overlay_limit: form.overlay_limit,
    }));
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Build a SetChargingProfile payload from the charger-details form.
 * @param {object} form
 * @param {Date} [now]
 */
export function buildChargingProfilePayload(form, now = new Date()) {
  const mode = form.mode || 'fallback';
  const numberPhases = asInt(form.number_phases, 3);
  const unit = form.charging_rate_unit || 'W';
  let connectorId = asInt(form.connector_id, 0);

  if (mode === 'cap' || form.purpose === 'ChargePointMaxProfile') {
    connectorId = 0;
  }

  const base = {
    chargingProfileId: now.getTime(),
    connectorId,
  };

  if (mode === 'fallback') {
    const fallbackLimit = requireLimit(form.fallback_limit, 'Fallback limit');
    return withScheduleTimes({
      ...base,
      stackLevel: 0,
      chargingProfilePurpose: 'TxDefaultProfile',
      chargingProfileKind: 'Absolute',
      chargingSchedule: {
        chargingRateUnit: unit,
        chargingSchedulePeriod: [period(0, fallbackLimit, numberPhases)],
      },
    }, now, FALLBACK_HOLD_SECS);
  }

  if (mode === 'timed') {
    const overlaySecs = asInt(form.duration, 0);
    if (overlaySecs < MIN_OVERLAY_SECS) {
      throw new Error(`Timed limit must be at least ${MIN_OVERLAY_SECS} seconds`);
    }
    const overlayLimit = requireLimit(form.overlay_limit, 'Timed limit');
    const fallbackLimit = requireLimit(form.fallback_limit, 'Fallback limit');
    const duration = overlaySecs + FALLBACK_HOLD_SECS;
    return withScheduleTimes({
      ...base,
      stackLevel: 0,
      chargingProfilePurpose: 'TxDefaultProfile',
      chargingProfileKind: 'Absolute',
      chargingSchedule: {
        chargingRateUnit: unit,
        chargingSchedulePeriod: [
          period(0, overlayLimit, numberPhases),
          period(overlaySecs, fallbackLimit, numberPhases),
        ],
      },
    }, now, duration);
  }

  // Station-wide ChargePointMax cap (min-merge; cannot raise power)
  const schedulePeriods = (form.periods || []).map((p) => period(
    p.startPeriod,
    requireLimit(p.limit, 'Period limit'),
    p.numberPhases != null ? p.numberPhases : numberPhases,
  ));
  if (schedulePeriods.length === 0) {
    throw new Error('At least one schedule period is required');
  }

  const durationSec = asInt(form.duration, FALLBACK_HOLD_SECS);
  const profile = {
    ...base,
    stackLevel: asInt(form.stack_level, 5),
    chargingProfilePurpose: 'ChargePointMaxProfile',
    chargingProfileKind: form.profile_kind || 'Absolute',
    chargingSchedule: {
      chargingRateUnit: unit,
      chargingSchedulePeriod: schedulePeriods,
    },
  };

  if (profile.chargingProfileKind === 'Recurring' && form.recurrency_kind) {
    profile.recurrencyKind = form.recurrency_kind;
  }

  return withScheduleTimes(profile, now, durationSec);
}

export function profileSubmitMessage(form) {
  const mode = form.mode || 'fallback';
  if (mode === 'fallback') {
    return 'Fallback TxDefault set (stack 0, 7 days)';
  }
  if (mode === 'timed') {
    return `Timed TxDefault set (${form.duration}s then fallback for 7 days, stack 0)`;
  }
  return `ChargePointMax cap set (stack ${form.stack_level})`;
}
